import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import axios from 'axios';
import { GlobalState } from '../../GlobalState';
import { AiFillStar, AiFillPlayCircle, AiFillPauseCircle, AiFillStop, AiOutlineMail, AiOutlineLoading3Quarters } from 'react-icons/ai';
import { FaRegThumbsUp, FaThumbsUp, FaRegComment, FaRegBookmark, FaBookmark } from 'react-icons/fa';
import { BsTwitter, BsFacebook, BsLinkedin, BsLink45Deg } from 'react-icons/bs';
import { RiShareCircleFill } from 'react-icons/ri';
import { TiSocialLinkedinCircular } from 'react-icons/ti';
import { MdBookmarkBorder } from 'react-icons/md';
import marked from 'marked';

// =============================================
// MEDIUM DESIGN TOKENS
// =============================================
const mediumTheme = {
  colors: {
    text: {
      primary: '#f4f6f8',
      secondary: '#a3acb2',
      light: '#6b7479'
    },
    background: {
      white: '#0f1216',
      light: 'rgba(255, 255, 255, 0.025)',
      border: 'rgba(255, 255, 255, 0.06)',
      hover: 'rgba(255, 255, 255, 0.04)'
    },
    accent: {
      green: '#5bb39e',
      lightGreen: 'rgba(91, 179, 158, 0.1)',
      red: '#f87171'
    }
  },
  typography: {
    fontFamily: {
      serif: 'charter, Georgia, Cambria, "Times New Roman", Times, serif',
      sansSerif: '"Lato", sans-serif'
    },
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '21px',
      '2xl': '28px',
      '3xl': '34px',
      '4xl': '42px'
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.4,
      relaxed: 1.6,
      loose: 1.8
    }
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px'
  }
};

// =============================================
// STYLED COMPONENTS
// =============================================
const StyledMainContainer = styled.div`
  max-width: 1200px;
  padding-top: 2rem;
  padding-left: 6rem;
  padding-right: 6rem;
  margin: 0 auto;
  background: ${mediumTheme.colors.background.white};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${mediumTheme.breakpoints.tablet}) {
    padding: ${mediumTheme.spacing['2xl']} ${mediumTheme.spacing.lg};
  }

  @media (max-width: ${mediumTheme.breakpoints.mobile}) {
    padding: ${mediumTheme.spacing.xl} ${mediumTheme.spacing.md};
  }
`;

const PaddingContent = styled.div`
  padding: 0;
`;

const MemberAlert = styled(Alert)`
  background-color: ${mediumTheme.colors.background.light} !important;
  border: 1px solid ${mediumTheme.colors.background.border} !important;
  border-radius: 8px !important;
  margin-bottom: ${mediumTheme.spacing.xl};
  padding: ${mediumTheme.spacing.md} ${mediumTheme.spacing.lg} !important;
  
  p {
    margin: 0 !important;
    font-size: ${mediumTheme.typography.fontSize.sm};
    color: ${mediumTheme.colors.text.secondary};
    font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  }
  
  a {
    color: ${mediumTheme.colors.accent.green};
    text-decoration: none;
    font-weight: ${mediumTheme.typography.fontWeight.medium};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${mediumTheme.spacing.md};
  margin-bottom: ${mediumTheme.spacing.xl};
`;

const CircleImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const NamePlate = styled.div`
  font-weight: ${mediumTheme.typography.fontWeight.medium};
  font-size: ${mediumTheme.typography.fontSize.base};
  color: ${mediumTheme.colors.text.primary};
  margin-bottom: 2px;
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
`;

const WrappedDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${mediumTheme.spacing.xs};
  font-size: ${mediumTheme.typography.fontSize.sm};
  color: ${mediumTheme.colors.text.secondary};
  flex-wrap: wrap;
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  
  span {
    color: ${mediumTheme.colors.text.light};
  }
`;

const AudioControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${mediumTheme.spacing.sm};
  margin-top: ${mediumTheme.spacing.sm};
`;

const AudioButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${mediumTheme.spacing.xs};
  font-size: ${mediumTheme.typography.fontSize.sm};
  color: ${props => props.green ? mediumTheme.colors.accent.green : 
                    props.red ? mediumTheme.colors.accent.red : 
                    mediumTheme.colors.text.secondary};
  padding: ${mediumTheme.spacing.xs} ${mediumTheme.spacing.sm};
  border-radius: 4px;
  transition: background-color 0.2s ease;
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};

  &:hover {
    background-color: ${props => props.green ? mediumTheme.colors.accent.lightGreen : mediumTheme.colors.background.hover};
  }

  svg {
    font-size: 14px;
  }
`;

const ArticleDivider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${mediumTheme.colors.background.border};
  margin: ${mediumTheme.spacing.xl} 0;
`;

const BlogTitle = styled.h1`
  font-family: ${mediumTheme.typography.fontFamily.serif};
  font-size: ${mediumTheme.typography.fontSize['4xl']};
  font-weight: ${mediumTheme.typography.fontWeight.bold};
  line-height: ${mediumTheme.typography.lineHeight.tight};
  color: ${mediumTheme.colors.text.primary};
  margin: ${mediumTheme.spacing.xl} 0 ${mediumTheme.spacing.lg};
  letter-spacing: -0.02em;

  @media (max-width: ${mediumTheme.breakpoints.mobile}) {
    font-size: ${mediumTheme.typography.fontSize['3xl']};
  }
`;

const BlogSubTitle = styled.h2`
  font-family: ${mediumTheme.typography.fontFamily.serif};
  font-size: ${mediumTheme.typography.fontSize.xl};
  font-weight: ${mediumTheme.typography.fontWeight.normal};
  line-height: ${mediumTheme.typography.lineHeight.relaxed};
  color: ${mediumTheme.colors.text.secondary};
  margin-bottom: ${mediumTheme.spacing.xl};
`;

const BlogDisplayImage = styled.img`
  width: 100%;
  height: auto;
  margin: ${mediumTheme.spacing.xl} 0 ${mediumTheme.spacing.sm};
  border-radius: 4px;
`;

const BlogPhotoCredit = styled.figcaption`
  font-size: ${mediumTheme.typography.fontSize.sm};
  color: ${mediumTheme.colors.text.secondary};
  text-align: center;
  font-style: italic;
  margin-bottom: ${mediumTheme.spacing.xl};
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
`;

const BlogCard = styled.div`
  margin: ${mediumTheme.spacing.xl} 0;
`;

const BlogPost = styled.article`
  margin: 0;
`;

const BlogContent = styled.div`
  align-items: start;
  font-family: ${mediumTheme.typography.fontFamily.serif};
  font-size: ${mediumTheme.typography.fontSize.xl};
  line-height: ${mediumTheme.typography.lineHeight.loose};
  color: ${mediumTheme.colors.text.primary};
  margin-bottom: ${mediumTheme.spacing.xl};
  padding: ${mediumTheme.spacing['2xl']} ${mediumTheme.spacing.lg};

  @media (max-width: ${mediumTheme.breakpoints.mobile}) {
    font-size: ${mediumTheme.typography.fontSize.lg};
    padding: ${mediumTheme.spacing.xl} 0;
  }

  p {
    margin-bottom: ${mediumTheme.spacing.xl};

    ${props => props.isFirst && `
      &:first-of-type::first-letter {
        font-size: 4em;
        line-height: 0.8;
        float: left;
        margin: 0.1em 0.1em 0 0;
        font-weight: 400;
        color: ${mediumTheme.colors.text.primary};
      }

      @media (max-width: 768px) {
        &:first-of-type::first-letter {
          font-size: 2.6em;
        }
      }
    `}
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${mediumTheme.typography.fontFamily.serif};
    font-weight: ${mediumTheme.typography.fontWeight.semibold};
    margin: ${mediumTheme.spacing['2xl']} 0 ${mediumTheme.spacing.lg};
    line-height: ${mediumTheme.typography.lineHeight.tight};
    color: ${mediumTheme.colors.text.primary};
  }

  h2 {
    font-size: ${mediumTheme.typography.fontSize['2xl']};
  }

  h3 {
    font-size: ${mediumTheme.typography.fontSize.xl};
  }

  blockquote {
    border-left: 3px solid ${mediumTheme.colors.text.primary};
    padding-left: ${mediumTheme.spacing.lg};
    margin: ${mediumTheme.spacing.xl} 0;
    font-style: italic;
    color: ${mediumTheme.colors.text.secondary};
  }

  code {
    background-color: ${mediumTheme.colors.background.light};
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 0.9em;
    color: ${mediumTheme.colors.text.primary};
  }

  pre {
    background-color: ${mediumTheme.colors.background.light};
    padding: ${mediumTheme.spacing.lg};
    border-radius: 4px;
    overflow-x: auto;
    max-width: 100%;
    margin: ${mediumTheme.spacing.xl} 0;
    border: 1px solid ${mediumTheme.colors.background.border};
    -webkit-overflow-scrolling: touch;

    code {
      background: none;
      padding: 0;
    }

    @media (max-width: ${mediumTheme.breakpoints.mobile}) {
      padding: ${mediumTheme.spacing.md};
      font-size: 13px;
      border-radius: 6px;
    }
  }

  a {
    color: ${mediumTheme.colors.accent.green};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  ul, ol {
    margin: ${mediumTheme.spacing.xl} 0;
    padding-left: ${mediumTheme.spacing.xl};
  }

  li {
    margin-bottom: ${mediumTheme.spacing.sm};
  }

  li:has(> input[type="checkbox"]) {
    list-style: none;
    margin-left: -${mediumTheme.spacing.lg};
    display: flex;
    align-items: baseline;
    gap: ${mediumTheme.spacing.sm};
  }

  input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    border: 1px solid ${mediumTheme.colors.background.border};
    border-radius: 3px;
    background: ${mediumTheme.colors.background.light};
    position: relative;
    top: 2px;
    cursor: default;

    &:checked {
      background: ${mediumTheme.colors.accent.green};
      border-color: ${mediumTheme.colors.accent.green};
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 0px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  img {
    max-width: 100%;
    height: auto;
    margin: ${mediumTheme.spacing.xl} 0;
    border-radius: 4px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${mediumTheme.spacing.xl} 0;
    font-family: ${mediumTheme.typography.fontFamily.sansSerif};
    font-size: ${mediumTheme.typography.fontSize.base};
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  thead {
    background: ${mediumTheme.colors.accent.lightGreen};
  }

  th, td {
    border: 1px solid ${mediumTheme.colors.background.border};
    padding: ${mediumTheme.spacing.sm} ${mediumTheme.spacing.md};
    text-align: left;
    vertical-align: top;
    line-height: ${mediumTheme.typography.lineHeight.normal};
  }

  th {
    font-weight: ${mediumTheme.typography.fontWeight.semibold};
    color: ${mediumTheme.colors.text.primary};
  }

  tbody tr:nth-child(even) {
    background: ${mediumTheme.colors.background.hover};
  }
`;

// Newsletter Section
const BlogNewsletter = styled.form`
  background-color: ${mediumTheme.colors.background.light};
  border-radius: 8px;
  padding: ${mediumTheme.spacing.xl};
  margin: ${mediumTheme.spacing['3xl']} 0;
  text-align: center;
  border: 1px solid ${mediumTheme.colors.background.border};

  h3 {
    font-size: ${mediumTheme.typography.fontSize.xl};
    font-weight: ${mediumTheme.typography.fontWeight.semibold};
    color: ${mediumTheme.colors.text.primary};
    margin-bottom: ${mediumTheme.spacing.sm};
    font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  }

  @media (max-width: ${mediumTheme.breakpoints.mobile}) {
    display: none;
  }
`;

const NewsletterContent = styled.div`
  margin-bottom: ${mediumTheme.spacing.lg};
  
  p {
    font-size: ${mediumTheme.typography.fontSize.base};
    color: ${mediumTheme.colors.text.secondary};
    margin-bottom: ${mediumTheme.spacing.sm};
    font-family: ${mediumTheme.typography.fontFamily.sansSerif};
    
    &.author {
      font-size: ${mediumTheme.typography.fontSize.sm};
      color: ${mediumTheme.colors.text.light};
      font-style: italic;
    }
  }

  u {
    text-decoration: underline;
    color: ${mediumTheme.colors.accent.green};
  }
`;

const NewsletterForm = styled.div`
  display: flex;
  gap: ${mediumTheme.spacing.sm};
  max-width: 400px;
  margin: 0 auto ${mediumTheme.spacing.lg};

  @media (max-width: ${mediumTheme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: rgba(15, 18, 22, 0.65);
  color: ${mediumTheme.colors.text.primary};
  font-size: ${mediumTheme.typography.fontSize.base};
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &:focus {
    border-color: rgba(91, 179, 158, 0.45);
    box-shadow: 0 0 0 3px rgba(91, 179, 158, 0.15);
    background: rgba(15, 18, 22, 0.85);
  }

  &::placeholder {
    color: ${mediumTheme.colors.text.light};
  }
`;

const NewsletterButton = styled.button`
  background-color: #206a5d;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 12px 18px;
  border-radius: 10px;
  font-size: ${mediumTheme.typography.fontSize.base};
  font-weight: ${mediumTheme.typography.fontWeight.semibold};
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.18s ease, transform 0.12s ease,
    box-shadow 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${mediumTheme.spacing.xs};
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: 0 8px 22px rgba(32, 106, 93, 0.28);

  &:hover {
    background-color: #267a6b;
    transform: translateY(-1px);
    box-shadow: 0 10px 26px rgba(32, 106, 93, 0.4);
  }

  svg {
    font-size: 18px;
  }
`;

const NewsletterDisclaimer = styled.p`
  font-size: ${mediumTheme.typography.fontSize.xs};
  color: ${mediumTheme.colors.text.light};
  text-align: center;
  line-height: ${mediumTheme.typography.lineHeight.normal};
  margin: 0;
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
`;

// Related Posts Section
const RelatedPostsSection = styled.div`
  background-color: ${mediumTheme.colors.background.light};
  padding: ${mediumTheme.spacing.xl} 0;
  margin-top: ${mediumTheme.spacing['3xl']};
  border-radius: 8px;
`;

const RelatedPostCard = styled.div`
  background: ${mediumTheme.colors.background.light};
  border: 1px solid ${mediumTheme.colors.background.border};
  border-radius: 12px;
  padding: ${mediumTheme.spacing.lg};
  margin-bottom: ${mediumTheme.spacing.lg};
  transition: border-color 0.2s ease, transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${mediumTheme.colors.background.hover};
    border-color: rgba(91, 179, 158, 0.25);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const RelatedPostHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${mediumTheme.spacing.md};
  margin-bottom: ${mediumTheme.spacing.lg};
`;

const RelatedPostContent = styled.div`
  display: flex;
  gap: ${mediumTheme.spacing.lg};
  margin-bottom: ${mediumTheme.spacing.md};

  @media (max-width: ${mediumTheme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const RelatedPostText = styled.div`
  flex: 1;
  
  h4 {
    font-size: ${mediumTheme.typography.fontSize.lg};
    font-weight: ${mediumTheme.typography.fontWeight.semibold};
    line-height: ${mediumTheme.typography.lineHeight.normal};
    margin-bottom: ${mediumTheme.spacing.sm};
    color: ${mediumTheme.colors.text.primary};
    font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  }

  a {
    color: ${mediumTheme.colors.text.primary};
    text-decoration: none;
    
    &:hover {
      color: ${mediumTheme.colors.accent.green};
    }
  }

  p {
    color: ${mediumTheme.colors.text.secondary};
    font-size: ${mediumTheme.typography.fontSize.base};
    line-height: ${mediumTheme.typography.lineHeight.relaxed};
    margin: 0;
    font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  }
`;

const RelatedPostImage = styled.img`
  width: 200px;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;

  @media (max-width: ${mediumTheme.breakpoints.mobile}) {
    width: 100%;
    height: 200px;
  }
`;

const RelatedPostMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${mediumTheme.spacing.sm};
  border-top: 1px solid ${mediumTheme.colors.background.border};
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: ${mediumTheme.typography.fontSize.sm};
  padding: ${mediumTheme.spacing.xs} ${mediumTheme.spacing.sm};
  border-radius: 12px;
  background-color: ${mediumTheme.colors.background.border};
  color: ${mediumTheme.colors.text.secondary};
  margin-right: ${mediumTheme.spacing.sm};
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
`;

const ReadMoreButton = styled.button`
  background: none;
  border: 1px solid ${mediumTheme.colors.background.border};
  color: ${mediumTheme.colors.text.secondary};
  padding: ${mediumTheme.spacing.sm} ${mediumTheme.spacing.lg};
  border-radius: 20px;
  font-size: ${mediumTheme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  margin: ${mediumTheme.spacing.xl} auto 0;
  display: block;
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};

  &:hover:not(:disabled) {
    background-color: ${mediumTheme.colors.accent.green};
    border-color: ${mediumTheme.colors.accent.green};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Sticky Footer
const StickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: ${mediumTheme.colors.background.white};
  border-top: 1px solid ${mediumTheme.colors.background.border};
  padding: ${mediumTheme.spacing.md} 0;
  margin-top: ${mediumTheme.spacing.xl};
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`;

const StickyContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EngagementActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${mediumTheme.spacing.lg};
`;

const EngagementButton = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${mediumTheme.spacing.xs};
  color: ${mediumTheme.colors.text.secondary};
  font-size: ${mediumTheme.typography.fontSize.sm};
  padding: ${mediumTheme.spacing.xs} ${mediumTheme.spacing.sm};
  border-radius: 4px;
  transition: all 0.2s ease;
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};

  &:hover {
    color: ${mediumTheme.colors.text.primary};
    background-color: ${mediumTheme.colors.background.hover};
  }

  svg {
    font-size: 16px;
  }
`;

// Tooltip shown above the engagement buttons. Default-hidden; appears on
// hover of the parent button. Used to warn anonymous users that clicking
// will redirect to login before they actually get redirected.
const Tooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #1a1e23;
  color: #f4f6f8;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-family: ${mediumTheme.typography.fontFamily.sansSerif};
  font-weight: 400;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.18s ease, visibility 0.18s ease;
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);

  /* little arrow pointing down at the button */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #1a1e23;
  }

  ${EngagementButton}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const SocialShare = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${mediumTheme.spacing.sm};
`;

const ShareButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${mediumTheme.spacing.xs};
  border-radius: 4px;
  color: ${mediumTheme.colors.accent.green};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${mediumTheme.colors.accent.lightGreen};
  }

  svg {
    font-size: 18px;
  }
`;

// Admin Controls
const AdminControls = styled.div`
  margin-top: ${mediumTheme.spacing.xl};
  padding: ${mediumTheme.spacing.lg};
  border: 1px solid ${mediumTheme.colors.background.border};
  border-radius: 8px;
  background-color: ${mediumTheme.colors.background.light};
  display: flex;
  gap: ${mediumTheme.spacing.md};
  align-items: center;

  input[type="checkbox"] {
    transform: scale(1.2);
  }

  label {
    font-size: ${mediumTheme.typography.fontSize.sm};
    color: ${mediumTheme.colors.text.secondary};
    margin-left: ${mediumTheme.spacing.sm};
  }

  button {
    background-color: ${mediumTheme.colors.accent.red};
    color: white;
    border: none;
    padding: ${mediumTheme.spacing.xs} ${mediumTheme.spacing.md};
    border-radius: 4px;
    font-size: ${mediumTheme.typography.fontSize.sm};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #dc4444;
    }
  }
`;

// =============================================
// MAIN COMPONENT
// =============================================
const MainContainer = ({ 
  detailArticle, 
  user, 
  articles, 
  timeFormater, 
  readTime, 
  viewComment, 
  setViewComment,
  isAdmin,
  isLoggedIn,
  deleteArticle,
  handleCheck
}) => {
  const { _id, likes, title, subtitle, description, images, markdown, comments, commentCount } = detailArticle;
  const globalState = useContext(GlobalState);
  const [token] = globalState.token;
  const history = useHistory();

  // Audio states
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioPaused, setAudioPaused] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState('');
  const audioRef = useRef(null);          // <audio> element for OpenAI MP3 playback
  const audioUrlRef = useRef(null);       // blob URL for cleanup
  const fallbackUtteranceRef = useRef(null); // Web Speech utterance for anonymous users
  const [postLikes, setPostLikes] = useState(likes || 0);
  const [hasLiked, setHasLiked] = useState(!!detailArticle.liked);
  const [hasSaved, setHasSaved] = useState(!!detailArticle.saved);
  const [likePending, setLikePending] = useState(false);
  const [savePending, setSavePending] = useState(false);

  // Keep local toggle state in sync when the article reloads (route change
  // brings a new detailArticle with fresh liked/saved/likes from the server).
  useEffect(() => {
    setPostLikes(detailArticle.likes || 0);
    setHasLiked(!!detailArticle.liked);
    setHasSaved(!!detailArticle.saved);
  }, [detailArticle._id, detailArticle.liked, detailArticle.saved, detailArticle.likes]);

  // Newsletter signup state. status: 'idle' | 'submitting' | 'success' | 'error'
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  
  // Related posts states
  const [idx, setIdx] = useState(4);
  const [moreArticles, setMoreArticles] = useState(true);
  const [recentPosts, setRecentPosts] = useState([]);

  const avatar = user.avatar || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT0k6I8WItSjK0JTttL3FwACOA6yugI29xvLw&usqp=CAU";

  // Clean up audio resources on unmount or article change
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [_id]);

  const playWebSpeechFallback = () => {
    const synth = window.speechSynthesis;
    if (!synth) {
      setAudioError('Audio not supported in this browser.');
      return;
    }
    const u = new SpeechSynthesisUtterance(markdown || '');
    u.rate = 0.95;
    u.onend = () => {
      setAudioPlaying(false);
      setAudioPaused(false);
    };
    fallbackUtteranceRef.current = u;
    synth.speak(u);
    setAudioPlaying(true);
    setAudioPaused(false);
  };

  const playOpenAIVoice = async () => {
    setAudioLoading(true);
    setAudioError('');
    try {
      const res = await axios.post(
        '/api/tts/synthesize',
        { text: markdown || '' },
        { headers: { Authorization: token }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setAudioPlaying(false);
        setAudioPaused(false);
      };
      await audio.play();
      setAudioPlaying(true);
      setAudioPaused(false);
    } catch (err) {
      // Surface what went wrong with OpenAI, then fall back to the free
      // browser voice so the user still gets audio playback.
      let msg = 'Using browser voice (premium unavailable).';
      if (err.response?.status === 429) {
        msg = 'Daily premium listen reached — using browser voice.';
      } else if (err.response?.data) {
        // axios blob errors need decoding
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.msg) msg = `${parsed.msg} — using browser voice.`;
          console.error('Audio error response:', parsed);
        } catch (parseErr) {
          console.error('Error parsing audio error response:', parseErr);
        }
      }
      setAudioError(msg);
      playWebSpeechFallback();
    } finally {
      setAudioLoading(false);
    }
  };

  const handlePlay = () => {
    if (audioLoading) return;
    // Resume case
    if (audioPaused && audioRef.current) {
      audioRef.current.play();
      setAudioPaused(false);
      setAudioPlaying(true);
      return;
    }
    if (audioPaused && window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
      setAudioPaused(false);
      setAudioPlaying(true);
      return;
    }
    // Fresh play
    if (isLoggedIn) {
      playOpenAIVoice();
    } else {
      playWebSpeechFallback();
    }
  };

  const handlePause = () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    } else if (window.speechSynthesis?.speaking) {
      window.speechSynthesis.pause();
    }
    setAudioPaused(true);
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setAudioPlaying(false);
    setAudioPaused(false);
  };

  // Related posts
  useEffect(() => {
    const shuffleArray = (arr) => arr.sort(() => 0.5 - Math.random());
    const filtered = shuffleArray([...articles])
      .filter((article) => article._id !== _id)
      .slice(0, idx);
    setRecentPosts(filtered);
  }, [articles, idx, _id]);

  const handleReadMore = () => {
    const newIdx = idx + 2;
    setIdx(newIdx);
    if (articles.length <= newIdx) {
      setMoreArticles(false);
    }
  };

  const handleLikeClick = async () => {
    if (likePending) return;
    if (!token) {
      history.push('/login');
      return;
    }
    setLikePending(true);
    // Optimistic toggle so the icon feels instant. Reverts on failure.
    const prevLiked = hasLiked;
    const prevCount = postLikes;
    setHasLiked(!prevLiked);
    setPostLikes(prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1);
    try {
      const res = await axios.post(`/api/articles/${_id}/like`, null, {
        headers: { Authorization: token },
      });
      setHasLiked(!!res.data?.liked);
      setPostLikes(res.data?.totalLikes ?? prevCount);
    } catch (err) {
      setHasLiked(prevLiked);
      setPostLikes(prevCount);
      console.error('Like failed:', err.response?.data?.msg || err.message);
    } finally {
      setLikePending(false);
    }
  };

  const handleSaveClick = async () => {
    if (savePending) return;
    if (!token) {
      history.push('/login');
      return;
    }
    setSavePending(true);
    const prevSaved = hasSaved;
    setHasSaved(!prevSaved);
    try {
      const res = await axios.post(`/api/articles/${_id}/save`, null, {
        headers: { Authorization: token },
      });
      setHasSaved(!!res.data?.saved);
    } catch (err) {
      setHasSaved(prevSaved);
      console.error('Save failed:', err.response?.data?.msg || err.message);
    } finally {
      setSavePending(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (newsletterStatus === 'submitting') return;
    setNewsletterStatus('submitting');
    setNewsletterMessage('');
    try {
      const res = await axios.post('/api/subscribers', {
        email: newsletterEmail,
        source: 'article-inline',
      });
      setNewsletterStatus('success');
      setNewsletterMessage(res.data?.msg || 'Check your inbox to confirm.');
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterMessage(err.response?.data?.msg || 'Something went wrong. Try again?');
    }
  };

  // Share functions
  const handleShare = async (platform) => {
    const url = window.location.href;
    const articleTitle = title;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${articleTitle}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
        break;
      case 'copy':
        await navigator.clipboard.writeText(url);
        break;
    }
  };

  return (
    <StyledMainContainer>
      <PaddingContent>
        <section>
          {user.name && (
            <MemberAlert variant="light">
              <p>
                You have <strong>1</strong> free member-only story left this month.{' '}
                <a href="/register">Sign up for Medium and get an extra one.</a>
              </p>
            </MemberAlert>
          )}

          <AuthorSection>
            <CircleImage src={avatar} alt="author" />
            <AuthorInfo>
              <NamePlate>Author: {user.name || "Will Smith"}</NamePlate>
              <WrappedDate>
                <span>{timeFormater}</span>
                <span>·</span>
                <span>
                  {readTime > 60
                    ? `${Math.floor(readTime / 60)} ${Math.floor(readTime / 60) === 1 ? 'Hour' : 'Hours'}${
                        readTime % 60 ? ` ${readTime % 60} min` : ''
                      } read`
                    : `${readTime} min read`}
                  <AiFillStar style={{ marginLeft: '4px' }} />
                </span>
              </WrappedDate>
              <AudioControls>
                {audioLoading ? (
                  <AudioButton green disabled>
                    <AiOutlineLoading3Quarters style={{ animation: 'spin 1s linear infinite' }} />
                    Loading…
                  </AudioButton>
                ) : !audioPlaying ? (
                  <AudioButton green onClick={handlePlay}>
                    <AiFillPlayCircle />
                    Listen
                  </AudioButton>
                ) : audioPaused ? (
                  <AudioButton green onClick={handlePlay}>
                    <AiFillPlayCircle />
                    Resume
                  </AudioButton>
                ) : (
                  <AudioButton green onClick={handlePause}>
                    <AiFillPauseCircle />
                    Pause
                  </AudioButton>
                )}
                {audioPlaying && (
                  <AudioButton red onClick={handleStop}>
                    <AiFillStop />
                    Stop
                  </AudioButton>
                )}
                {audioError && (
                  <span style={{ color: '#e0625e', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                    {audioError}
                  </span>
                )}
              </AudioControls>
            </AuthorInfo>
          </AuthorSection>

          <ArticleDivider />

          <BlogTitle>{title}</BlogTitle>
          {subtitle && <BlogSubTitle>{subtitle}</BlogSubTitle>}
          
          {images?.url && (
            <>
              <BlogDisplayImage src={images.url} alt={title} />
              <BlogPhotoCredit>
                Photo Credit by <u>{user.name || "Anonymous"}</u>
              </BlogPhotoCredit>
            </>
          )}

          <BlogCard>
            <BlogPost>
              {
                description && (
                  <BlogContent isFirst>
                  {description}
                </BlogContent>
                )
              }
              <BlogContent 
                dangerouslySetInnerHTML={{ __html: marked(markdown || '') }}
              />

              <StickyFooter>
                <StickyContent>
                  <EngagementActions>
                    <EngagementButton
                      onClick={handleLikeClick}
                      disabled={likePending}
                      style={hasLiked ? { color: mediumTheme.colors.accent.green } : {}}
                    >
                      {hasLiked ? <FaThumbsUp /> : <FaRegThumbsUp />}
                      {postLikes}
                      {!token && <Tooltip>Sign in to like — saves to your account</Tooltip>}
                    </EngagementButton>
                    <EngagementButton onClick={() => setViewComment(true)}>
                      <FaRegComment />
                      {commentCount ?? comments?.length ?? 0}
                    </EngagementButton>
                    <EngagementButton
                      onClick={handleSaveClick}
                      disabled={savePending}
                      style={hasSaved ? { color: mediumTheme.colors.accent.green } : {}}
                    >
                      {hasSaved ? <FaBookmark /> : <FaRegBookmark />}
                      {hasSaved ? 'Saved' : 'Save'}
                      {!token && <Tooltip>Sign in to save — syncs across devices</Tooltip>}
                    </EngagementButton>
                  </EngagementActions>
                  
                  <SocialShare>
                    <ShareButton onClick={() => handleShare('facebook')}>
                      <BsFacebook />
                    </ShareButton>
                    <ShareButton onClick={() => handleShare('twitter')}>
                      <BsTwitter />
                    </ShareButton>
                    <ShareButton onClick={() => handleShare('linkedin')}>
                      <TiSocialLinkedinCircular />
                    </ShareButton>
                    <ShareButton onClick={() => handleShare('copy')}>
                      <RiShareCircleFill />
                    </ShareButton>
                  </SocialShare>
                </StickyContent>
              </StickyFooter>

              <BlogNewsletter onSubmit={handleNewsletterSubmit}>
                <div>
                  <h3>Get new posts in your inbox</h3>
                  <NewsletterContent>
                    <p className="author">By D. Hosea</p>
                    <p>
                      One email when I publish something new. No schedule, no digests,
                      no spam — just the post.
                    </p>
                  </NewsletterContent>
                  {newsletterStatus !== 'success' && (
                    <NewsletterForm>
                      <NewsletterInput
                        name="email"
                        placeholder="Your email"
                        type="email"
                        required
                        value={newsletterEmail}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                        disabled={newsletterStatus === 'submitting'}
                      />
                      <NewsletterButton type="submit" disabled={newsletterStatus === 'submitting'}>
                        <AiOutlineMail />
                        {newsletterStatus === 'submitting' ? 'Subscribing…' : 'Get this newsletter'}
                      </NewsletterButton>
                    </NewsletterForm>
                  )}
                  {newsletterMessage && (
                    <NewsletterDisclaimer
                      style={{
                        color: newsletterStatus === 'error' ? '#e0625e' : mediumTheme.colors.accent.green,
                        fontSize: mediumTheme.typography.fontSize.sm,
                      }}
                    >
                      {newsletterMessage}
                    </NewsletterDisclaimer>
                  )}
                  <NewsletterDisclaimer>
                    You'll get a confirmation email first. One-click unsubscribe in every
                    email. I don't share your address.
                  </NewsletterDisclaimer>
                </div>
              </BlogNewsletter>
            </BlogPost>

            {isAdmin && isLoggedIn && (
              <AdminControls>
                <input
                  type="checkbox"
                  checked={true}
                  onChange={() => handleCheck(_id)}
                />
                <label>Mark as checked</label>
                <button onClick={() => deleteArticle(_id, images?.public_id)}>
                  Delete Article
                </button>
              </AdminControls>
            )}
          </BlogCard>
        </section>

        {/* Related Posts */}
        <RelatedPostsSection>
          {recentPosts.map((article) => {
            const articleDate = article.createdAt
              ? new Date(article.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "";
            const articleWordCount = (article.markdown?.length || 0) + 700;
            const articleReadTime = Math.max(
              1,
              Math.round(articleWordCount / 238)
            );
            const articleAvatar =
              article.postedBy?.avatar?.url ||
              article.postedBy?.avatar ||
              avatar;
            const articleCategory =
              (Array.isArray(article.categories) && article.categories[0]) ||
              "Software";
            return (
            <RelatedPostCard key={article._id}>
              <RelatedPostHeader>
                <CircleImage
                  src={articleAvatar}
                  alt="author"
                />
                <div>
                  <NamePlate>{article.postedBy?.name || "D. Hosea"}</NamePlate>
                  <span style={{color: mediumTheme.colors.text.secondary, fontSize: mediumTheme.typography.fontSize.sm}}>
                    {articleDate}
                  </span>
                </div>
              </RelatedPostHeader>

              <RelatedPostContent>
                <RelatedPostText>
                  <Link to={`/blog/${article._id}`}>
                    <h4>{article.title}</h4>
                  </Link>
                  <p>{article.description}</p>
                </RelatedPostText>
                <RelatedPostImage
                  src={article.images?.url || "https://tateeda.com/wp-content/uploads/2020/05/2.png"}
                  alt="post"
                />
              </RelatedPostContent>

              <RelatedPostMeta>
                <div>
                  <Tag>{articleCategory}</Tag>
                  <span style={{color: mediumTheme.colors.text.secondary, fontSize: mediumTheme.typography.fontSize.sm}}>
                    {articleReadTime} min read
                  </span>
                </div>
                <MdBookmarkBorder style={{ fontSize: "24px", color: mediumTheme.colors.text.secondary }} />
              </RelatedPostMeta>
            </RelatedPostCard>
            );
          })}
          
          <ReadMoreButton
            onClick={handleReadMore}
            disabled={!moreArticles}
          >
            {moreArticles ? 'Read more' : 'No more articles'}
          </ReadMoreButton>
        </RelatedPostsSection>
      </PaddingContent>
    </StyledMainContainer>
  );
};

export default MainContainer;